import { Link } from '@tanstack/react-router';
import { CodeBlock, Callout } from '../../components';

export default function Content() {
  return (
    <>
      <p>
        To reduce GLB file size, start with the textures: resize 4K maps to 2K or 1K and convert them to WebP or AVIF.
        Then compress the geometry with Draco or Meshopt and strip out unused and duplicate data. On a typical textured
        model those steps together make the file 60–90% smaller with little visible difference.
      </p>
      <p>
        Which step matters most depends on what is taking up the space, so measure first. Below: how to find the
        problem, eight techniques with their savings and trade-offs, and the tools that apply them.
      </p>

      <h2>Step 1: find out what's making your GLB big</h2>
      <p>
        A <Link to="/blog/what-is-a-glb-file">GLB file</Link> holds three kinds of data: textures (images), geometry
        (vertex positions, normals, UVs and triangle indices) and animation. Each needs a different fix.
      </p>
      <ul>
        <li>
          <strong>In the browser:</strong> drop the file into our free <Link to="/gltf-validator">glTF validator</Link>.
          It runs the official Khronos glTF-Validator and reports vertex and triangle counts, draw calls, the
          extensions in use, and any errors.
        </li>
        <li>
          <strong>On the command line:</strong> the open-source gltf-transform CLI prints a table of every mesh,
          material, texture and animation in the file.
        </li>
      </ul>
      <CodeBlock language="bash">{`npx @gltf-transform/cli inspect model.glb`}</CodeBlock>
      <p>
        The textures table lists each image's format, resolution, file size and estimated GPU memory; the meshes table
        lists vertex counts and attributes. Add up the texture sizes and compare them to the file size. The Khronos{' '}
        <em>DamagedHelmet</em> sample, for example, is a 3.8 MB GLB with five 2048×2048 JPEG textures totalling about
        3.2 MB (roughly 85% of the file) and only about 14,500 vertices. That's a texture problem; compressing the
        geometry alone would barely help.
      </p>
      <p>Rules of thumb:</p>
      <ul>
        <li>
          <strong>Textures dominate</strong> in product models, characters and most PBR assets. Start with techniques
          1 and 2.
        </li>
        <li>
          <strong>Geometry dominates</strong> in CAD exports, 3D scans, photogrammetry and sculpts. Start with
          techniques 3 and 6.
        </li>
        <li>
          <strong>Animation dominates</strong> in rigged characters with long, baked clips. See technique 8.
        </li>
      </ul>

      <h2>1. Resize textures</h2>
      <p>
        Oversized textures are the most common reason a GLB is too big, and a phone or laptop screen rarely shows the
        detail in a 4096×4096 map. Going from 4K to 2K leaves 25% of the pixels; 4K to 1K leaves about 6%. It also
        saves memory: once decoded, a 2048×2048 texture takes about 22 MB of GPU memory with mipmaps and a 4096×4096
        texture about 89 MB, whether it was stored as PNG, JPEG or WebP.
      </p>
      <ul>
        <li>
          <strong>Typical impact:</strong> often the biggest single win on texture-heavy models. Resizing DamagedHelmet's
          textures from 2K to 1K took the file from 3.8 MB to 1.25 MB (67% smaller) on its own.
        </li>
        <li>
          <strong>Trade-off:</strong> fine detail is lost up close. Keep textures larger on hero objects people zoom
          into; small parts and background props can go to 1K or 512.
        </li>
        <li>
          <strong>How:</strong> set a maximum texture size in our <Link to="/">GLB compressor</Link>, or use{' '}
          <code>gltf-transform resize</code>. That command only resizes PNG and JPEG, so run it before converting to
          WebP.
        </li>
      </ul>
      <CodeBlock language="bash">{`npx @gltf-transform/cli resize model.glb resized.glb --width 1024 --height 1024`}</CodeBlock>

      <h2>2. Convert textures to WebP or AVIF</h2>
      <p>
        Many exporters write PNG textures, which are lossless and large. WebP and AVIF are much smaller at similar
        visual quality, and glTF supports them through the <code>EXT_texture_webp</code> and{' '}
        <code>EXT_texture_avif</code> extensions.
      </p>
      <ul>
        <li>
          <strong>Typical impact:</strong> large, and larger still when the sources are PNG. Even DamagedHelmet, whose
          textures were already JPEG, went from 3.8 MB to 1.8 MB (52% smaller) with WebP at full 2K resolution.
        </li>
        <li>
          <strong>Trade-offs:</strong> the viewer must support the extension. three.js's GLTFLoader reads both; check
          older engines and native viewers. AVIF usually compresses further but is slower to encode and decode, so WebP
          is the safer default. If a normal map shows blocky shading, raise its quality.
        </li>
        <li>
          <strong>How:</strong> choose WebP or AVIF and a quality setting in our compressor, or run{' '}
          <code>gltf-transform webp</code> / <code>gltf-transform avif</code>.
        </li>
      </ul>
      <CodeBlock language="bash">{`npx @gltf-transform/cli webp model.glb webp.glb --quality 80`}</CodeBlock>
      <Callout title="What about KTX2 and Basis Universal?">
        <p>
          PNG, JPEG, WebP and AVIF are decoded to full-size bitmaps in GPU memory. KTX2 textures with Basis Universal
          compression (<code>KHR_texture_basisu</code>) stay compressed on the GPU, which cuts GPU memory and speeds up
          texture upload on mobile, but they aren't always smaller on disk. As the gltf-transform docs put it, KTX2
          optimizes VRAM usage and performance, while AVIF and WebP optimize transmission size. You can create KTX2
          with gltf-transform's <code>etc1s</code>/<code>uastc</code> commands (they need KTX-Software installed) or
          gltfpack's <code>-tc</code> flag. Our compressor doesn't output KTX2 yet; it's planned.
        </p>
      </Callout>

      <h2>3. Compress geometry with Draco or Meshopt</h2>
      <p>
        <Link to="/blog/draco-compression">Draco</Link> (<code>KHR_draco_mesh_compression</code>) encodes vertex and
        index data far more compactly than raw floats. It only touches geometry; textures and animation are left as
        they are.
      </p>
      <ul>
        <li>
          <strong>Typical impact:</strong> large on geometry-heavy files, small on texture-heavy ones. The Khronos{' '}
          <em>DragonAttenuation</em> sample (6.4 MB, about 135,000 triangles, mostly geometry) dropped to 1.17 MB with
          Draco alone, 82% smaller. DamagedHelmet only went from 3.8 MB to 3.3 MB (13%), because its weight is in the
          textures.
        </li>
        <li>
          <strong>Trade-offs:</strong> the viewer needs a Draco decoder. Google's WebAssembly decoder is about 290 KB
          plus a 60 KB JavaScript wrapper (around 100 KB gzipped), downloaded once and cached, and decoding takes CPU
          time on load. Quantization is lossy, but the default 14 bits for positions is invisible on most models;
          precise CAD parts may need more (see our{' '}
          <Link to="/docs/support/advanced-draco-settings">Draco settings guide</Link>).
        </li>
        <li>
          <strong>How:</strong> enable Draco in our compressor and set the compression level and quantization bits, run{' '}
          <code>gltf-transform draco</code>, or tick Draco in Blender's glTF exporter.
        </li>
      </ul>
      <CodeBlock language="bash">{`npx @gltf-transform/cli draco model.glb draco.glb`}</CodeBlock>
      <p>
        <strong>Meshopt</strong> (<code>EXT_meshopt_compression</code>) is the main alternative. Its decoder is tiny
        (about 25 KB) and very fast, it also compresses animation and morph targets, and its output is meant to be
        gzipped or Brotli-compressed again by your server. Files are usually somewhat larger: the dragon came out at
        1.89 MB with Meshopt (1.59 MB gzipped) against 1.17 MB with Draco. Pick Draco when download size matters most,
        Meshopt for animated models or fast decoding on low-end devices. See{' '}
        <Link to="/blog/draco-vs-meshopt">Draco vs Meshopt</Link> for the details. Our compressor uses Draco only; for
        Meshopt use <code>gltf-transform meshopt</code> or gltfpack.
      </p>
      <Callout title="Compress geometry last">
        <p>
          gltf-transform decodes Draco when it reads a file, so running another command on a Draco-compressed GLB
          writes it back without Draco. In our test the 1.17 MB Draco dragon grew to 6.13 MB after a WebP pass, and the
          CLI warned that further compression will be lossy (quantizing twice adds error twice). Resize and convert
          textures first, compress geometry as the final step, and keep an uncompressed original.
        </p>
      </Callout>

      <h2>4. Remove unused and duplicate data</h2>
      <p>
        Exporters and repeated editing leave dead weight: materials and textures nothing uses, unread vertex
        attributes, empty nodes, and duplicate copies of the same texture, material or mesh.
      </p>
      <ul>
        <li>
          <strong>Typical impact:</strong> nothing on a clean export, a lot on a messy one (for example, the same
          texture embedded once per material).
        </li>
        <li>
          <strong>Trade-offs:</strong> effectively lossless. Pruning can remove empty nodes your code uses as anchors;{' '}
          <code>gltf-transform prune</code> has a <code>--keep-leaves</code> option for that.
        </li>
        <li>
          <strong>How:</strong> <code>gltf-transform prune</code> removes anything not referenced by a scene, and{' '}
          <code>gltf-transform dedup</code> merges duplicate accessors, textures, materials, meshes and skins.
        </li>
      </ul>

      <h2>5. Weld, merge and instance</h2>
      <ul>
        <li>
          <strong>Weld</strong> merges identical vertices so triangles share them through the index buffer. The file
          gets smaller, the GPU's vertex cache works better, and simplification (technique 6) needs it.
        </li>
        <li>
          <strong>Merge meshes and flatten nodes</strong> combine parts that share a material and remove empty parents.
          This mainly speeds up rendering by cutting draw calls; the file size change is usually small.
        </li>
        <li>
          <strong>Instancing</strong> stores a repeated mesh (bolts, chairs, trees) once and draws every copy with{' '}
          <code>EXT_mesh_gpu_instancing</code>, which three.js supports. Run dedup first so identical copies are
          detected.
        </li>
      </ul>
      <p>
        The trade-off: merging and flattening remove individual nodes, so skip them if your code moves or hides parts
        by name. All of these are <Link to="/docs/support/geometry-transform-passes">geometry passes</Link> in our
        compressor, and <code>weld</code>, <code>join</code>, <code>flatten</code> and <code>instance</code> in
        gltf-transform.
      </p>

      <h2>6. Simplify (decimate) the mesh</h2>
      <p>
        Compression stores the same triangles more efficiently; simplification removes triangles. Scans, photogrammetry,
        sculpts and CAD tessellations often carry far more triangles than a web viewer can show, and fewer triangles
        also render faster.
      </p>
      <ul>
        <li>
          <strong>Typical impact:</strong> depends on how dense the mesh is. Halving the dragon's triangle count before
          Draco gave 0.92 MB, against 1.17 MB for Draco alone.
        </li>
        <li>
          <strong>Trade-offs:</strong> lossy, and visible if pushed too far. Check silhouettes, UV seams and shading up
          close. Weld first, or the simplifier can't collapse split vertices.
        </li>
        <li>
          <strong>How:</strong> Blender's <Link to="/blog/blender-reduce-glb-file-size">Decimate modifier</Link>, or{' '}
          <code>gltf-transform simplify</code>, where <code>--ratio</code> is the fraction of vertices to keep and{' '}
          <code>--error</code> caps how far the shape may drift (so it can stop short of the ratio).
        </li>
      </ul>
      <CodeBlock language="bash">{`npx @gltf-transform/cli weld model.glb welded.glb
npx @gltf-transform/cli simplify welded.glb simplified.glb --ratio 0.5 --error 0.001`}</CodeBlock>

      <h2>7. Quantize vertex data</h2>
      <p>
        Quantization stores 32-bit float attributes as 16- or 8-bit integers (<code>KHR_mesh_quantization</code>).
        Draco and Meshopt already quantize internally, so on its own it matters mainly when you want smaller geometry
        without a decoder. Quantized data also stays smaller in GPU memory, not just on disk.
      </p>
      <ul>
        <li>
          <strong>Typical impact:</strong> a moderate cut in geometry size, and gzip or Brotli on your server become
          more effective.
        </li>
        <li>
          <strong>Trade-offs:</strong> slight precision loss (gltf-transform defaults to 14 bits for positions, 10 for
          normals, 12 for UVs), and the viewer must support <code>KHR_mesh_quantization</code>. three.js does.
        </li>
        <li>
          <strong>How:</strong> <code>gltf-transform quantize model.glb out.glb</code>. gltfpack quantizes by default.
        </li>
      </ul>

      <h2>8. Trim and resample animation</h2>
      <p>
        Exporters often bake animation into a keyframe on every frame, including bones that barely move. Unused clips,
        control bones and redundant keyframes all end up in the file, and Draco doesn't compress any of it.
      </p>
      <ul>
        <li>
          <strong>Typical impact:</strong> none on static models; significant on characters with long baked clips.
        </li>
        <li>
          <strong>Trade-offs:</strong> resampling is nearly lossless; deleting clips or bones is only safe if nothing
          uses them.
        </li>
        <li>
          <strong>How:</strong> <code>gltf-transform resample</code> removes redundant keyframes, and Meshopt
          compresses what's left. In Blender, delete unused actions and use the exporter's sampling and animation
          optimization options (see our <Link to="/blog/blender-reduce-glb-file-size">Blender guide</Link>).
        </li>
      </ul>

      <h2>What to expect</h2>
      <p>
        Here are the two Khronos samples from above, processed with gltf-transform 4.5 (default settings unless noted).
        It's one test, not a benchmark, but it shows why the order of attack matters:
      </p>
      <table>
        <thead>
          <tr>
            <th>Model and steps</th>
            <th>Size</th>
            <th>Change</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>DamagedHelmet, original (textures ≈ 85% of file)</td><td>3.77 MB</td><td>–</td></tr>
          <tr><td>Draco only</td><td>3.29 MB</td><td>−13%</td></tr>
          <tr><td>WebP only, textures kept at 2K</td><td>1.82 MB</td><td>−52%</td></tr>
          <tr><td>Resize to 1K + WebP</td><td>0.93 MB</td><td>−75%</td></tr>
          <tr><td>Resize to 1K + WebP + Draco</td><td>0.45 MB</td><td>−88%</td></tr>
          <tr><td>DragonAttenuation, original (mostly geometry)</td><td>6.40 MB</td><td>–</td></tr>
          <tr><td>Meshopt only</td><td>1.89 MB</td><td>−70%</td></tr>
          <tr><td>Draco only</td><td>1.17 MB</td><td>−82%</td></tr>
          <tr><td>Simplify to 50% + Draco</td><td>0.92 MB</td><td>−86%</td></tr>
        </tbody>
      </table>
      <p>By model type, roughly:</p>
      <table>
        <thead>
          <tr>
            <th>Model type</th>
            <th>Usually dominated by</th>
            <th>Biggest wins</th>
            <th>What to expect</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>CAD exports, 3D scans, photogrammetry, sculpts</td>
            <td>Geometry</td>
            <td>Draco or Meshopt, weld, simplify</td>
            <td>Large: often 70–90% from geometry compression alone</td>
          </tr>
          <tr>
            <td>Game assets and characters</td>
            <td>A mix, plus animation</td>
            <td>Resize + WebP, Draco or Meshopt, resampling</td>
            <td>Moderate to large, depending on texture sizes</td>
          </tr>
          <tr>
            <td>Product models with 2K–4K PBR textures</td>
            <td>Textures</td>
            <td>Resize + WebP or AVIF</td>
            <td>Large once textures are handled; small with Draco alone</td>
          </tr>
          <tr>
            <td>Files already using Draco, Meshopt or KTX2</td>
            <td>Varies</td>
            <td>Texture resizing, if still large</td>
            <td>Little to gain; re-compressing costs quality</td>
          </tr>
        </tbody>
      </table>

      <h2>Tools compared</h2>
      <table>
        <thead>
          <tr>
            <th>Tool</th>
            <th>Best for</th>
            <th>Geometry</th>
            <th>Textures</th>
            <th>Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>compress-glb.com</td>
            <td>No install, batch uploads, presets, REST API</td>
            <td>Draco, weld, duplicate-vertex removal, simplify, instancing, merge</td>
            <td>Resize; WebP, AVIF, JPEG, PNG</td>
            <td>1 free credit, then credit packs</td>
          </tr>
          <tr>
            <td>gltf-transform CLI</td>
            <td>Full control and scripting</td>
            <td>Draco, Meshopt, quantize, every pass above</td>
            <td>Resize; WebP, AVIF; KTX2 with KTX-Software</td>
            <td>Free, open source; needs Node.js</td>
          </tr>
          <tr>
            <td>gltfpack</td>
            <td>Fast native Meshopt pipelines</td>
            <td>Meshopt, quantization, simplification (no Draco)</td>
            <td>KTX2 (<code>-tc</code>), WebP (<code>-tw</code>)</td>
            <td>Free, open source</td>
          </tr>
          <tr>
            <td>Blender glTF exporter</td>
            <td>Fixing the model at the source</td>
            <td>Draco; Meshopt from 5.2; Decimate before export</td>
            <td>JPEG or WebP, no resizing</td>
            <td>Free; one file at a time</td>
          </tr>
        </tbody>
      </table>
      <p>
        Our <Link to="/">online GLB compressor</Link> runs gltf-transform on our servers and applies Draco, texture
        resizing and conversion, and the geometry passes in one upload. The web app takes files up to 500 MB, a{' '}
        <Link to="/docs/api">REST API</Link> covers pipelines and larger files, and every output is checked with the
        Khronos glTF validator. Typical results are 60–90% smaller.
      </p>
      <p>
        On the command line, gltf-transform's <code>optimize</code> runs most of these techniques at once. By default
        it uses Meshopt, caps textures at 2048 px and simplifies with a very small error tolerance (turn that off with{' '}
        <code>--simplify false</code>). Our <Link to="/blog/gltf-transform-optimize">gltf-transform optimize guide</Link>{' '}
        explains the flags, and our <Link to="/blog/blender-reduce-glb-file-size">Blender guide</Link> covers fixing
        the model before export.
      </p>
      <CodeBlock language="bash">{`npx @gltf-transform/cli optimize model.glb optimized.glb \\
  --compress draco --texture-compress webp --texture-size 1024`}</CodeBlock>

      <h2>Checklist</h2>
      <ol>
        <li>Measure first: textures, geometry or animation?</li>
        <li>Resize textures to what the viewer actually shows (2K or 1K for most web use).</li>
        <li>Convert textures to WebP (or AVIF) and check the result up close.</li>
        <li>Prune and dedup unused and duplicate data.</li>
        <li>Weld, then simplify dense meshes if the silhouette holds up.</li>
        <li>Resample animation and drop clips and bones you don't use.</li>
        <li>Apply Draco or Meshopt as the last step.</li>
        <li>
          Validate the result in the <Link to="/gltf-validator">glTF validator</Link> and test it in your real viewer,
          making sure it loads the decoders it needs (see <Link to="/blog/threejs-load-glb">loading GLB in three.js</Link>{' '}
          or our <Link to="/blog/shopify-3d-models">Shopify 3D model guide</Link>).
        </li>
      </ol>

      <h2>FAQ</h2>
      <h3>Why is my GLB file so big?</h3>
      <p>
        Usually large, uncompressed textures: a few 4K PNG maps can add up to tens of megabytes. The other common causes
        are very dense meshes (scans, CAD, sculpts) and animation baked on every frame. <code>gltf-transform
        inspect</code> shows which one it is.
      </p>
      <h3>How do I reduce GLB file size without losing quality?</h3>
      <p>
        Start with the lossless steps: prune, dedup, weld and resample. Then apply the lossy ones moderately: WebP at
        quality 80 or higher, textures no larger than the viewer shows, and Draco at default quantization. Compare
        before and after up close.
      </p>
      <h3>Does Draco compression reduce quality?</h3>
      <p>
        Slightly, because it quantizes positions, normals and UVs. At default settings the difference is invisible on
        most models; raise the position bits for precise CAD parts or very large scenes.
      </p>
      <h3>Is GLB smaller than glTF?</h3>
      <p>
        A GLB is about the same size as a <code>.gltf</code> with separate <code>.bin</code> and image files, and
        roughly a quarter smaller than a <code>.gltf</code> with base64-embedded data. Our free{' '}
        <Link to="/gltf-to-glb">glTF to GLB converter</Link> packs a <code>.gltf</code> and its files into one GLB; see{' '}
        <Link to="/blog/gltf-vs-glb">glTF vs GLB</Link> for more.
      </p>
      <h3>Can I compress a GLB file online?</h3>
      <p>
        Yes. Our <Link to="/">GLB compressor</Link> resizes and converts textures, applies Draco and cleans up geometry
        in one upload; it needs a free account, which includes one free compression. gltf-transform and gltfpack are
        free if you're comfortable with the command line.
      </p>
    </>
  );
}
